/*
 * Beisat endless product loading (New Version HP-07).
 *
 * 1. Homepage feed  [data-beisat-feed]  (sections/beisat-product-feed.liquid)
 *    Mixes several collections by weight. Pages come from the Section Rendering API:
 *    /collections/<handle>?page=N&section_id=beisat-feed-page (24 products a page).
 * 2. Collection pages  [data-beisat-infinite]  (sections/main-collection.liquid)
 *    Follows the "Next" pagination link (keeps filters and sort) with ?section_id=<main>.
 *
 * New cards are wired up like other late cards (beisat.init + beisat:related-products:loaded
 * + Swishlist refresh). Filters re-render the collection grid and call beisat.init, which
 * re-binds the loader. Back from a product page restores the loaded pages and scroll.
 */
(() => {
  const FEED_PAGE_SIZE = 24;
  // Start the next page well before the end (phones scroll fast); was 800px.
  const ROOT_MARGIN = '1600px 0px';
  const parser = new DOMParser();

  const storage = {
    get(key) {
      try {
        return JSON.parse(sessionStorage.getItem(key) || 'null');
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch (e) {}
    },
  };

  const isBackNavigation = () => {
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      return Boolean(nav && nav.type === 'back_forward');
    } catch (e) {
      return false;
    }
  };

  // The Beisat app's WebView reloads the listing on back (navigation type "navigate"),
  // so also restore when we arrive straight from a product page soon after leaving.
  const RESTORE_WINDOW_MS = 30 * 60 * 1000;
  const cameBackFromProduct = (saved) => {
    try {
      const ref = document.referrer ? new URL(document.referrer) : null;
      return Boolean(ref && ref.origin === window.location.origin && ref.pathname.includes('/products/')
        && saved && saved.t && Date.now() - saved.t < RESTORE_WINDOW_MS);
    } catch (e) {
      return false;
    }
  };

  const withParams = (url, params) => {
    const u = new URL(url, window.location.origin);
    Object.entries(params).forEach(([k, v]) => u.searchParams.set(k, v));
    return u.pathname + u.search;
  };

  // A stalled request (phone network, Cloudflare) used to leave the loader - and its
  // skeleton cards - waiting forever; give up after 15s so the retry path takes over.
  const FETCH_TIMEOUT_MS = 15000;
  const fetchDoc = async (url, signal) => {
    const ctrl = new AbortController();
    const onAbort = () => ctrl.abort();
    if (signal) {
      if (signal.aborted) ctrl.abort();
      else signal.addEventListener('abort', onAbort, { once: true });
    }
    const timer = window.setTimeout(() => ctrl.abort(new DOMException('Timed out', 'TimeoutError')), FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(url, { signal: ctrl.signal, credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return parser.parseFromString(await res.text(), 'text/html');
    } finally {
      window.clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', onAbort);
    }
  };
  const wait = (ms) => new Promise((resolve) => { window.setTimeout(resolve, ms); });

  // A batch wrapper with display: contents keeps cards as grid items while giving
  // the card scripts a scope that holds only the new cards.
  const makeBatch = () => {
    const batch = document.createElement('div');
    batch.className = 'beisat-feed__batch';
    return batch;
  };

  const hydrate = (root) => {
    root.querySelectorAll('[data-cc-animate]').forEach((node) => {
      node.removeAttribute('data-cc-animate');
      node.classList.remove('cc-animate-init', 'cc-animate-in', 'fade-in', 'fade-in-up');
    });
    if (window.beisat && typeof window.beisat.init === 'function') window.beisat.init(root);
    document.dispatchEvent(new CustomEvent('beisat:related-products:loaded', { detail: { section: root } }));
    const sw = window.SWishlist;
    try {
      if (sw && typeof sw.loadProductCardRuntime === 'function') sw.loadProductCardRuntime();
      if (sw && typeof sw.updateButtonsWatcher === 'function') sw.updateButtonsWatcher(root);
      if (sw && typeof sw.refreshAllButtonStates === 'function') sw.refreshAllButtonStates();
    } catch (e) {}
  };

  // Skeleton cards in the grid while a page loads (instead of only a spinner line).
  const SKELETONS = 4;
  const skeletonHtml = '<div class="beisat-skel-card" data-beisat-skel aria-hidden="true">'
    + '<div class="beisat-skel-card__img"></div><div class="beisat-skel-card__line"></div>'
    + '<div class="beisat-skel-card__line beisat-skel-card__line--short"></div></div>';
  const showSkeletons = (grid, on) => {
    if (!grid) return;
    grid.querySelectorAll('[data-beisat-skel]').forEach((el) => el.remove());
    if (on) grid.insertAdjacentHTML('beforeend', skeletonHtml.repeat(SKELETONS));
  };

  const announce = (kind, page, items) => {
    document.dispatchEvent(new CustomEvent('beisat:feed:page', { detail: { kind, page, items } }));
  };

  // Same distance as the observer's rootMargin: with a smaller one a sentinel resting
  // between the two never triggered again (the observer only reports changes).
  const NEAR_PX = 1600;
  const nearViewport = (el) => {
    const r = el.getBoundingClientRect();
    return r.top < window.innerHeight + NEAR_PX && r.bottom > -NEAR_PX;
  };

  /* Shared loader shell: sentinel observer, loading/error UI, back-navigation restore. */
  const createLoader = ({ root, key, loadPage, onFinish, restoreFromProduct = false, grid = null }) => {
    const sentinel = root.querySelector('[data-beisat-feed-sentinel]');
    const loadingEl = root.querySelector('[data-beisat-feed-loading]');
    const errorEl = root.querySelector('[data-beisat-feed-error]');
    const retry = root.querySelector('[data-beisat-feed-retry]');
    let controller = new AbortController();
    let busy = false;
    let finished = false;
    let pages = 0;
    let observer;

    const setState = (state) => {
      // With a grid, skeleton cards stand in for the loading line.
      if (loadingEl) loadingEl.hidden = grid ? true : state !== 'loading';
      if (errorEl) errorEl.hidden = state !== 'error';
      showSkeletons(grid, state === 'loading');
    };

    const finish = () => {
      finished = true;
      setState('idle');
      if (observer) observer.disconnect();
      root.setAttribute('data-finished', '');
      if (onFinish) onFinish();
    };

    const load = async () => {
      if (busy || finished || !root.isConnected) return false;
      busy = true;
      setState('loading');
      try {
        const more = await loadPage(controller.signal);
        pages += 1;
        storage.set(key, { pages, y: window.scrollY });
        setState('idle');
        if (!more) finish();
        return true;
      } catch (e) {
        // Aborted (leaving the page): clear the skeletons; anything else shows the retry button.
        setState(e.name === 'AbortError' ? 'idle' : 'error');
        return false;
      } finally {
        busy = false;
        // A short page can leave the sentinel in view; keep going.
        if (!finished && sentinel && sentinel.isConnected && nearViewport(sentinel) && errorEl && errorEl.hidden) {
          window.requestAnimationFrame(() => load());
        }
      }
    };

    // Re-observe so the observer reports the sentinel's current state again (a sentinel
    // that stayed in range never fires a new callback on its own).
    const rearm = () => {
      if (observer && sentinel && sentinel.isConnected && !finished) {
        observer.unobserve(sentinel);
        observer.observe(sentinel);
      }
    };

    const start = async () => {
      root.setAttribute('data-js', '');
      if (retry) retry.addEventListener('click', () => load());

      // Back from a product: load the same pages again, then return to the same place.
      // Any other visit starts fresh.
      const stored = storage.get(key);
      const saved = isBackNavigation() || (restoreFromProduct && cameBackFromProduct(stored)) ? stored : null;
      if (!saved) storage.set(key, { pages: 0, y: 0 });
      if (saved && (saved.pages > 0 || saved.y > 0)) {
        // Our position, not the browser's (it restores before the pages are back).
        try {
          window.history.scrollRestoration = 'manual';
        } catch (e) {}
        for (let i = 0; i < saved.pages && !finished; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          if (!(await load())) break;
        }
        // 'instant': the theme sets scroll-behavior: smooth on <html>.
        window.scrollTo({ top: saved.y || 0, behavior: 'instant' });
      }

      if (finished || !sentinel) return;
      if (!('IntersectionObserver' in window)) return; // pagination / end link stay as fallback
      observer = new IntersectionObserver((entries) => {
        if (!root.isConnected) {
          observer.disconnect();
          return;
        }
        if (entries.some((e) => e.isIntersecting)) load();
      }, { rootMargin: ROOT_MARGIN });
      observer.observe(sentinel);
    };

    window.addEventListener('pagehide', () => {
      const saved = storage.get(key);
      if (saved) storage.set(key, { ...saved, y: window.scrollY, t: Date.now() });
      controller.abort();
    });
    // Back to a page the browser kept in memory (bfcache): the aborted controller would
    // stop every later page and leave skeletons on screen. Start fresh and carry on.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted || finished) return;
      controller = new AbortController();
      busy = false;
      setState('idle');
      rearm();
    });

    return { start, finish };
  };

  /* ------------------------------------------------------------------ */
  /* 1. Homepage feed                                                    */
  /* ------------------------------------------------------------------ */
  const bindFeed = (root) => {
    if (root.dataset.feedBound) return;
    root.dataset.feedBound = 'true';

    const grid = root.querySelector('[data-beisat-feed-grid]');
    const sourcesEl = root.querySelector('[data-beisat-feed-sources]');
    if (!grid || !sourcesEl) return;

    let sources;
    try {
      sources = JSON.parse(sourcesEl.textContent).map((s) => ({
        url: s.url,
        handle: s.handle,
        weight: Math.max(1, Number(s.weight) || 1),
        nextPage: Math.floor((s.offset || 0) / FEED_PAGE_SIZE) + 1,
        skip: (s.offset || 0) % FEED_PAGE_SIZE,
        buffer: [],
        done: false,
      }));
    } catch (e) {
      return;
    }
    if (!sources.length) return;

    const maxItems = Number(root.dataset.maxItems) || 200;
    const batchSize = Number(root.dataset.batchSize) || 20;
    const recentAfter = Number(root.dataset.recentAfter) || 0;
    const weightTotal = sources.reduce((sum, s) => sum + s.weight, 0);
    const seen = new Set([...grid.querySelectorAll('[data-product-id]')].map((el) => el.dataset.productId));
    let count = grid.querySelectorAll('[data-beisat-feed-item]').length;
    let page = 1;

    const fillNow = async (src, need, signal) => {
      while (!src.done && src.buffer.length < need) {
        // eslint-disable-next-line no-await-in-loop
        const doc = await fetchDoc(withParams(src.url, { page: src.nextPage, section_id: 'beisat-feed-page' }), signal);
        const pageEl = doc.querySelector('[data-beisat-feed-page]');
        if (!pageEl) {
          src.done = true;
          break;
        }
        let items = [...pageEl.querySelectorAll('[data-beisat-feed-item]')];
        if (src.skip) {
          items = items.slice(src.skip);
          src.skip = 0;
        }
        items.forEach((item) => {
          item.dataset.feedSource = src.handle; // which collection it came from (analytics, QA)
        });
        src.buffer.push(...items.filter((item) => item.dataset.available !== 'false'));
        src.nextPage += 1;
        if (pageEl.dataset.hasNext !== 'true') src.done = true;
      }
    };
    // One fill at a time per source, so a background prefetch and a scroll-triggered load
    // never fetch the same page twice; a failed fill doesn't block the next one.
    const fill = (src, need, signal) => {
      const run = (src.queue || Promise.resolve()).catch(() => {}).then(() => fillNow(src, need, signal));
      src.queue = run;
      return run;
    };

    // Weighted round robin over the sources' buffers, skipping products already shown.
    const take = (limit) => {
      const out = [];
      let progressed = true;
      while (out.length < limit && progressed) {
        progressed = false;
        for (const src of sources) {
          for (let k = 0; k < src.weight && out.length < limit; k += 1) {
            const item = src.buffer.shift();
            if (!item) break;
            progressed = true;
            if (seen.has(item.dataset.productId)) continue;
            seen.add(item.dataset.productId);
            out.push(item);
          }
        }
      }
      return out;
    };

    const placeRecent = () => {
      if (!recentAfter || root.dataset.recentPlaced) return;
      const items = grid.querySelectorAll('[data-beisat-feed-item]');
      if (items.length < recentAfter) return;
      const recent = document.querySelector('.recent-products');
      const recentSection = recent && recent.closest('.shopify-section');
      if (!recentSection || root.contains(recentSection)) return;
      const slot = document.createElement('div');
      slot.className = 'beisat-feed__slot';
      slot.appendChild(recentSection);
      items[recentAfter - 1].after(slot);
      root.dataset.recentPlaced = 'true';
    };

    const loadPage = async (signal) => {
      const limit = Math.min(batchSize, maxItems - count);
      if (limit <= 0) return false;
      const rounds = Math.ceil(limit / weightTotal) + 1;
      await Promise.all(sources.map((src) => fill(src, src.weight * rounds, signal)));
      const items = take(limit);
      if (items.length) {
        const batch = makeBatch();
        items.forEach((item) => batch.appendChild(document.importNode(item, true)));
        grid.appendChild(batch);
        hydrate(batch);
        count += items.length;
        page += 1;
        placeRecent();
        announce('home', page, items.length);
      }
      const exhausted = sources.every((src) => src.done && !src.buffer.length);
      const more = items.length > 0 && !exhausted && count < maxItems;
      // Buffer the next batch in the background, so scrolling on usually finds it ready.
      if (more) sources.forEach((src) => fill(src, src.weight * rounds * 2, signal).catch(() => {}));
      return more;
    };

    placeRecent();
    createLoader({
      root,
      key: `beisat-feed:${window.location.pathname}`,
      loadPage,
      grid,
    }).start();
  };

  /* ------------------------------------------------------------------ */
  /* 2. Collection pages                                                 */
  /* ------------------------------------------------------------------ */
  const bindInfinite = (holder) => {
    if (holder.dataset.infiniteBound) return;
    holder.dataset.infiniteBound = 'true';

    const sectionId = holder.dataset.sectionId;
    // The holder carries data-section-id itself, so start from its parent.
    const scope = (holder.parentElement && holder.parentElement.closest('[data-section-id]')) || document;
    const grid = scope.querySelector('.product-grid');
    let nextUrl = holder.dataset.nextUrl;
    if (!grid || !sectionId || !nextUrl) {
      holder.setAttribute('data-finished', '');
      return;
    }
    let page = 1;

    // The next page is fetched as soon as the current one is in, so scrolling on usually
    // finds it ready. One request per page: the prefetch and the load share it.
    let pending = null; // { url, doc: Promise }
    const fetchPage = (url, signal) => fetchDoc(withParams(url, { section_id: sectionId }), signal);
    const request = (url, signal) => {
      if (pending && pending.url === url) return pending.doc;
      const doc = fetchPage(url, signal);
      pending = { url, doc };
      doc.catch(() => {
        if (pending && pending.doc === doc) pending = null;
      });
      return doc;
    };
    const prefetch = (signal) => {
      if (nextUrl) request(nextUrl, signal).catch(() => {});
    };

    const loadPage = async (signal) => {
      if (!nextUrl) return false;
      let doc;
      try {
        doc = await request(nextUrl, signal);
      } catch (e) {
        if (e.name === 'AbortError') throw e;
        // One quiet retry (timeouts, a 429 from too many quick pages) before the retry button.
        await wait(1500);
        doc = await fetchPage(nextUrl, signal);
      }
      pending = null;
      const newGrid = doc.querySelector('.product-grid');
      const newHolder = doc.querySelector('[data-beisat-infinite]');
      nextUrl = newHolder ? newHolder.dataset.nextUrl : '';
      if (newGrid) {
        const batch = makeBatch();
        [...newGrid.children].forEach((card) => {
          const id = card.querySelector('[data-product-id]')?.dataset.productId || card.dataset.productId;
          if (id && grid.querySelector(`[data-product-id="${CSS.escape(id)}"]`)) return;
          batch.appendChild(document.importNode(card, true));
        });
        grid.appendChild(batch);
        hydrate(batch);
        page += 1;
        announce('collection', page, batch.children.length);
      }
      prefetch(signal);
      return Boolean(nextUrl);
    };

    const loader = createLoader({
      root: holder,
      key: `beisat-infinite:${window.location.pathname}${window.location.search}`,
      loadPage,
      restoreFromProduct: true,
      grid,
    });
    loader.start();
    // Page 2 once the first screen has settled.
    window.setTimeout(() => prefetch(), 1200);
  };

  const bindAll = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-beisat-feed]').forEach(bindFeed);
    scope.querySelectorAll('[data-beisat-infinite]').forEach(bindInfinite);
  };

  // Collection filters re-render the grid and call beisat.init(section): re-bind there.
  const base = window.beisat && window.beisat.init;
  if (typeof base === 'function' && !base.beisatFeedWrapped) {
    const wrapped = (root) => {
      base(root);
      bindAll(root);
    };
    wrapped.beisatFeedWrapped = true;
    window.beisat.init = wrapped;
  }

  bindAll(document);
  document.addEventListener('shopify:section:load', (event) => bindAll(event.target));
})();
